import { Box, Stack, TextField, Typography } from '@mui/material';
import { ICaseDetailsInputs } from 'API';
import { CustomRadioGroup, FileUpload, TextareaCounter } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY, FileCategories } from 'Enum';
import { useDictionary } from 'Hooks';
import { IndexContext } from 'Modules/CaseDetails/CaseContext';
import { useContext, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateError } from 'Regex';
import { convertStringBoolToBoolean } from 'Utils/BooleanStringHandler';
import { validateLettersNumbersAndSpacialChars } from 'Utils/validateLettersNumbersAndSpacialChars';

export const InjuryDetails = () => {
  const { index, fieldId } = useContext(IndexContext);
  const {
    register,
    formState: { errors },
  } = useFormContext<ICaseDetailsInputs>();

  const [textAreaDesc, setTextAreaDesc] = useState<string>('');

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}.injuryKindSection`;

  const [isInjuryDescribedInFile, description] = useWatch({
    name: [`${baseName}.isInjuryDescribedInFile`, `${baseName}.description`],
  });

  const fileTextOptions = [
    {
      id: true,
      value: dictionary.secondSectionInjuryDetailsFileOption,
    },
    {
      id: false,
      value: dictionary.secondSectionInjuryDetailsTextOption,
    },
  ];

  const isInjuryDescribedInFileBooleanValue = convertStringBoolToBoolean(
    isInjuryDescribedInFile
  );

  const injuryKindSectionServiceError = errors.caseDetails
    ? errors.caseDetails[index]?.injuryKindSection
    : undefined;

  useEffect(() => {
    if (description) setTextAreaDesc(description);
  }, [description]);

  return (
    <Stack sx={{ mt: '20px' }}>
      <CustomRadioGroup
        id={dictionary.secondSectionInjuryDetailsTitle}
        removeBold
        name={`${baseName}.isInjuryDescribedInFile` as const}
        options={fileTextOptions.map(({ id, value }) => ({
          value: id.toString(),
          label: value,
        }))}
      />
      {isInjuryDescribedInFileBooleanValue && (
        <Stack gap='10px' sx={{ mt: '20px' }}>
          <Typography>{dictionary.secondSectionFileTitle}</Typography>
          <FileUpload
            caseDetailsId={fieldId}
            fileLabel={dictionary.secondSectionFileAria}
            fileCategoryId={FileCategories.InjuryDescription}
            ariaLabel={dictionary.secondSectionFileAria}
            dataRequired
          />
        </Stack>
      )}
      {isInjuryDescribedInFileBooleanValue === false && (
        <Box
          sx={{
            position: 'relative',
          }}
        >
          <TextField
            id={`${dictionary.secondSectionTextAreaLabel}-${index}`}
            label={dictionary.secondSectionTextAreaLabel}
            sx={{ mt: '20px' }}
            inputProps={{
              maxLength: '500',
            }}
            {...register(
              `${baseName}.description` as `caseDetails.0.injuryKindSection.description`
            )}
            multiline
            rows={6}
            onChange={(event: any) => {
              setTextAreaDesc(event.target.value);
              validateLettersNumbersAndSpacialChars(event);
            }}
            role={
              Boolean(injuryKindSectionServiceError?.description)
                ? 'alert'
                : undefined
            }
            error={Boolean(injuryKindSectionServiceError?.description)}
            helperText={
              Boolean(injuryKindSectionServiceError?.description?.message) &&
              generateError(
                dictionary.secondSectionTextError,
                injuryKindSectionServiceError?.description?.message!
              )
            }
          />
          <TextareaCounter
            counter={textAreaDesc.length}
            limit={500}
            hasError={Boolean(injuryKindSectionServiceError?.description)}
          />
        </Box>
      )}
    </Stack>
  );
};
